import { FormEvent, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SEOHead } from '../../../components/seo/SEOHead';
import { ConsultationStatusBadge } from '../../../components/ui/StatusBadge';
import { Spinner } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Select, Textarea } from '../../../components/ui/Input';
import { api } from '../../../lib/api';
import { CONSULTATION_STATUS_LABELS, type ConsultationBooking, type ConsultationStatus } from '@bharat-consultancy/shared';

export default function ConsultationDetail() {
  const { id } = useParams<{ id: string }>();
  const [booking, setBooking] = useState<ConsultationBooking | null>(null);
  const [allowedTransitions, setAllowedTransitions] = useState<ConsultationStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [isSavingNote, setIsSavingNote] = useState(false);

  function loadBooking() {
    if (!id) return Promise.resolve();
    return api.admin.consultations.get(id).then((res) => {
      setBooking(res.booking);
      setNoteDraft(res.booking.adminNote ?? '');
    });
  }

  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    setLoadError(null);
    Promise.all([
      loadBooking(),
      api.admin.consultations
        .allowedTransitions(id)
        .then((res) => setAllowedTransitions(res.allowedTransitions as ConsultationStatus[])),
    ])
      .catch(() => setLoadError('Could not load this booking. Please refresh and try again.'))
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function handleStatusChange(newStatus: ConsultationStatus) {
    if (!id) return;
    setStatusUpdating(true);
    setActionError(null);
    try {
      await api.admin.consultations.updateStatus(id, newStatus);
      await loadBooking();
      const transitions = await api.admin.consultations.allowedTransitions(id);
      setAllowedTransitions(transitions.allowedTransitions as ConsultationStatus[]);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not update status.');
    } finally {
      setStatusUpdating(false);
    }
  }

  async function handleSaveNote(e: FormEvent) {
    e.preventDefault();
    if (!id) return;
    setIsSavingNote(true);
    setActionError(null);
    try {
      await api.admin.consultations.setNote(id, noteDraft.trim());
      await loadBooking();
    } catch {
      setActionError('Could not save note. Please try again.');
    } finally {
      setIsSavingNote(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="h-6 w-6 text-seal-blue" />
      </div>
    );
  }

  if (loadError) return <p className="text-red-600">{loadError}</p>;
  if (!booking) return <p className="text-ink/60">Consultation booking not found.</p>;

  return (
    <>
      <SEOHead title={`Consultation — ${booking.name} — Admin`} description="" canonicalPath={`/admin/consultations/${booking.id}`} />

      {actionError && <p className="mb-4 text-sm text-red-600">{actionError}</p>}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-ink">{booking.name}</h1>
          <p className="mt-1 text-ink/60">{booking.service?.title ?? 'General enquiry'}</p>
        </div>
        <ConsultationStatusBadge status={booking.status} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="rounded-lg border border-line bg-white p-5">
            <h2 className="font-display text-base font-semibold text-ink">Contact Details</h2>
            <dl className="mt-3 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-ink/50">Email</dt>
                <dd className="text-ink">{booking.email}</dd>
              </div>
              <div>
                <dt className="text-ink/50">Mobile</dt>
                <dd className="text-ink">{booking.mobile}</dd>
              </div>
              <div>
                <dt className="text-ink/50">WhatsApp</dt>
                <dd className="text-ink">{booking.whatsapp ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-ink/50">Preferred Time</dt>
                <dd className="text-ink">
                  {booking.preferredDate ? `${booking.preferredDate} ${booking.preferredTime ?? ''}` : 'Not specified'}
                </dd>
              </div>
              {booking.message && (
                <div className="sm:col-span-2">
                  <dt className="text-ink/50">What they want to discuss</dt>
                  <dd className="text-ink">{booking.message}</dd>
                </div>
              )}
            </dl>
          </div>

          <div className="rounded-lg border border-line bg-white p-5">
            <h2 className="font-display text-base font-semibold text-ink">Call Notes / Quotation Given</h2>
            <p className="mt-1 text-xs text-ink/50">
              Record what was discussed on the call and the quotation you proposed — visible only to admin staff.
            </p>
            <form onSubmit={handleSaveNote} className="mt-3 space-y-2">
              <Textarea
                rows={4}
                placeholder="e.g. Discussed GST registration requirements. Quoted ₹2,499 for Standard plan with return filing support."
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
              />
              <Button type="submit" size="sm" disabled={isSavingNote}>
                {isSavingNote ? 'Saving...' : 'Save Note'}
              </Button>
            </form>
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-lg border border-line bg-white p-5">
            <h2 className="font-display text-base font-semibold text-ink">Update Status</h2>
            <Select
              className="mt-3"
              disabled={statusUpdating || allowedTransitions.length === 0}
              value=""
              onChange={(e) => e.target.value && handleStatusChange(e.target.value as ConsultationStatus)}
            >
              <option value="">
                {allowedTransitions.length === 0 ? 'No further transitions' : 'Select new status...'}
              </option>
              {allowedTransitions.map((status) => (
                <option key={status} value={status}>
                  {CONSULTATION_STATUS_LABELS[status]}
                </option>
              ))}
            </Select>
          </div>

          <div className="rounded-lg border border-line bg-white p-5">
            <h2 className="font-display text-base font-semibold text-ink">Payment</h2>
            <dl className="mt-3 space-y-2 text-sm">
              <div className="stub-divider flex justify-between pt-2 font-semibold">
                <dt className="text-ink">Amount Paid</dt>
                <dd className="font-mono text-seal-blue">₹{booking.amount.toLocaleString('en-IN')}</dd>
              </div>
            </dl>
            <p className="mt-3 text-xs text-ink/40">Booked {new Date(booking.createdAt).toLocaleString('en-IN')}</p>
          </div>
        </div>
      </div>
    </>
  );
}
